import { Component, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import _ from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { inOutAnimation, routerTransition } from './animations';
import { User } from './models/user';
import { AppStateService } from './services/app-state.service';
import { UtilsService } from './services/utils.service';
import { FcmService } from './services/fcm.service';
import { BadgeService } from './services/badge.service';
import { UserService } from './services/user.service';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Network } from '@capacitor/network';
import { App, StateChangeListener, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Location } from '@angular/common';
import moment from 'moment';
declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [routerTransition, inOutAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent {
  title = 'a8';
  user: User;
  pendingInvitesCount: number = 0;
  subs: Subscription[] = [];

  flags = {
    authenticatedMode: false,
    sidenav: false,
    onProfile: false,
  };
  items: Observable<any[]>;

  constructor(
    private appState: AppStateService,
    private utils: UtilsService,
    private fcm: FcmService,
    private badgeService: BadgeService,
    private userService: UserService,
    public router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private zone: NgZone,
  ) {
    this.initializeApp();


    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        let urlParts = event.url.split('/');
        if (urlParts[1] == 'profile' && urlParts.length == 3) {
          this.flags.onProfile = true;
        }
      }
    });

    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide({ fadeOutDuration: 150 }).catch(() => {});

      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          if (this.user?.id) {
            this.userService.metrics(this.user.id).subscribe({
              next: (metrics) => {
                if (this.user) {
                  this.user.metrics = metrics;
                  this.badgeService.syncBadge(this.user, this.pendingInvitesCount);
                }
              },
              error: () => {
                this.badgeService.syncBadge(this.user, this.pendingInvitesCount);
              }
            });
          } else if (this.user) {
            this.badgeService.syncBadge(this.user, this.pendingInvitesCount);
          }
        }
      });

      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.zone.run(async () => {
          try {
            await Browser.close();
          } catch (e) {}

          const rawUrl = event.url || '';
          console.log('appUrlOpen received URL:', rawUrl);

          let slug = '';
          if (rawUrl.startsWith('activity8://') || rawUrl.startsWith('com.activity8.app://')) {
            slug = rawUrl.replace(/^(activity8:\/\/|com\.activity8\.app:\/\/)/, '/');
          } else if (rawUrl.includes('activity8.app')) {
            slug = rawUrl.split('activity8.app').pop() || '';
          } else if (rawUrl.includes('dev.activity8.com')) {
            slug = rawUrl.split('dev.activity8.com').pop() || '';
          } else if (rawUrl.includes('.app')) {
            slug = rawUrl.split('.app').pop() || '';
          }

          if (slug.startsWith('/stripe-success') || slug.startsWith('/invite-paid') || slug.includes('status=success') || slug.includes('session_id=')) {
            this.utils.alert.success('Payment Sent');
            this.router.navigateByUrl('/invites');
            return;
          }

          if (slug.startsWith('/stripe-cancel') || slug.includes('status=cancel')) {
            this.utils.alert.message('Checkout was cancelled.');
            return;
          }

          if (slug) {
            if (!slug.startsWith('/')) {
              slug = '/' + slug;
            }
            this.router.navigateByUrl(slug);
          }
        });
      });

      Browser.addListener('browserFinished', () => {
        this.zone.run(() => {
          if (this.user) {
            this.utils.appState.user.next(this.user);
          }
        });
      });      
      App.addListener('backButton', data => {
        if(window.location.href != null && window.location.href.toLowerCase().includes('/dashboard')){
          App.exitApp();
        }
        else if(this.flags.onProfile){
          // CODE DOESN"T MAKE SENSE TO ME BUT WORKS GOOD
          this.router.navigateByUrl('/dashboard', {skipLocationChange: true});
          this.location.back();
          this.flags.onProfile = false;
        }
        else{
          this.location.back();
        }
     });

      Network.addListener('networkStatusChange', status => {
        console.log('Network status changed', status);
      });
      
      const logCurrentNetworkStatus = async () => {
        const status = await Network.getStatus();
        if(status != null && !status.connected){
          this.utils.alert.error('Network Offline');
        }
        console.log('Network status:', status);
      };     
    }
  }

  initializeApp() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (typeof gtag === 'function') {
          try {
            gtag('config', 'UA-161580073-1', {
              page_path: event.urlAfterRedirects,
            });
          } catch (e) {
            console.warn('Analytics event skipped:', e);
          }
        }
      }
    });

    let initialMetricsFetched = false;
    const user = this.utils.appState.user.subscribe((resp) => {
      this.user = resp;
      if (this.user?.id && Capacitor.isNativePlatform()) {
        this.fcm.initPush();
        this.badgeService.syncBadge(this.user, this.pendingInvitesCount);
        if (!initialMetricsFetched) {
          initialMetricsFetched = true;
          this.userService.metrics(this.user.id).subscribe({
            next: (metrics) => {
              if (this.user) {
                this.user.metrics = metrics;
                this.badgeService.syncBadge(this.user, this.pendingInvitesCount);
              }
            },
            error: () => {}
          });
        }
      }
    });
    this.subs.push(user);

    const invites = this.utils.appState.invites.subscribe((invitesList: any[]) => {
      if (invitesList && Array.isArray(invitesList) && this.user?.id) {
        this.pendingInvitesCount = invitesList.filter((inv: any) => {
          const isPending = inv.status === 'pending' || inv.status === 'paid-cancel';
          const isForMe = inv.to?.id === this.user.id || (inv.to && String(inv.to.id) === String(this.user.id));
          const isUpcoming = !inv.datetime || moment(inv.datetime).isSameOrAfter(moment());
          return isPending && isForMe && isUpcoming;
        }).length;
        if (Capacitor.isNativePlatform()) {
          this.badgeService.syncBadge(this.user, this.pendingInvitesCount, invitesList);
        }
      }
    });
    this.subs.push(invites);

    const sidenav = this.appState.sidenav.subscribe((resp) => {
      this.flags.sidenav = resp;
    });
    this.subs.push(sidenav);
  }

  closeSideNav(): void {
    this.flags.sidenav = false;
    this.utils.appState.sidenav.next(false);
  }

  openSideNav(): void {
    let back = false;
    if (
      this.router.url.includes('/join') || this.router.url.includes('/messages/')
      // || (this.user != null && this.router.url.includes('/') && this.router.url != '/' + this.user.username)
      || (this.router.url.includes('/') && this.router.url.split('/').length > 3 && this.user != null)
      ){
      back = true;
    }


    if(!this.flags.sidenav && !back && this.user != null && this.user.app_state.showSetLocation == null && !this.router.url.includes('/')){
      this.flags.sidenav = true;
      this.utils.appState.sidenav.next(true);
    }

    if (back){
      this.location.back();
    }
  }

  getRouterState(outlet) {
    return outlet.activatedRouteData.state;
  }

  ngOnDestroy() {
    _.each(this.subs, (sub) => {
      sub.unsubscribe();
    });
  }
}
